import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Drawer, Form, Input, Space } from 'antd';
import { useState } from 'react';
import { createPlaceSchema, updatePlaceSchema, type PlaceDto, type PlaceSuggestionsResponse } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { useDebounced } from '@/hooks/useDebounced';
import { applyServerIssues, useZodForm } from '@/lib/zodForm';

/**
 * Create or edit a place, with the near-duplicate prompt inline.
 *
 * The `{ open, onClose, onSuccess }` convention from PAMS_CONVENTIONS §1, and every
 * validation rule comes from the shared schema through `useZodForm`.
 *
 * ─── The prompt is the point, not the block (FR-5.7.3) ───────────────────────────
 *
 * A global free-text gazetteer fragments into "Esplanade", "Esplanade Crossing" and
 * "Esplande" within a month, and then local-first autocomplete never gets good — which
 * is the whole premise of the design. So the operator is TOLD, at the moment they type
 * the name, that the neighbours exist. They may still create anyway; that is always
 * permitted, and the button says so plainly rather than hiding behind a confirmation.
 *
 * Two mechanisms, deliberately:
 *   • this dialog asks `GET /places/suggestions` while there is still a form to correct;
 *   • the server refuses a create with 409 unless `confirmDuplicate` is set, so a caller
 *     that never renders this screen still has to see the candidates once.
 */
export const PlaceDialog = ({
  open,
  mode,
  place,
  readOnly,
  onClose,
  onSuccess,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  place?: PlaceDto;
  readOnly: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const schema = mode === 'create' ? createPlaceSchema : updatePlaceSchema;
  const { form, field, submit, formErrors, setFormErrors } = useZodForm(schema);

  const [name, setName] = useState(place?.name ?? '');
  /**
   * Set once the operator has been shown the candidates and chosen to proceed. It is
   * NOT set from the start: a create that never surfaces a suggestion is the normal
   * case, and pre-confirming it would disable the server's backstop for everybody.
   */
  const [confirmed, setConfirmed] = useState(false);

  const trimmed = useDebounced(name).trim();
  const suggestions = useQuery({
    queryKey: queryKeys.places.suggestions(trimmed),
    queryFn: ({ signal }) =>
      api.get<PlaceSuggestionsResponse>('/places/suggestions', { query: { name: trimmed }, signal }),
    // Three characters is where a prefix starts meaning something; below it every place
    // in the master is a "match" and the prompt is noise.
    enabled: open && trimmed.length >= 3 && trimmed !== place?.name,
    staleTime: 30_000,
  });

  const candidates = suggestions.data?.suggestions ?? [];

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      mode === 'create'
        ? api.post<PlaceDto>('/places', { ...values, confirmDuplicate: confirmed })
        : api.patch<PlaceDto>(`/places/${place?.id ?? ''}`, { ...values, confirmDuplicate: confirmed }),
    onSuccess: () => {
      void message.success(mode === 'create' ? 'Place created' : 'Place updated');
      void queryClient.invalidateQueries({ queryKey: queryKeys.places.all });
      onSuccess?.();
      onClose();
    },
    onError: (error: ApiError) => {
      /*
       * A 409 carrying suggestions is not a form error — it is the backstop firing
       * because this dialog's own probe had not answered yet. Arm the confirmation and
       * say what happened, rather than showing a red validation message for a request
       * that was correct.
       */
      if (error.status === 409) {
        setConfirmed(true);
        setFormErrors([`${error.message}. Check the suggestions above, then save again to create it anyway.`]);
        return;
      }
      setFormErrors(applyServerIssues(form, error.details).concat(error.details ? [] : [error.message]));
    },
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={480}
      /* [P2] Opening a row without the write capability is VIEWING, and the title says
         so rather than promising an edit every control refuses. */
      title={mode === 'create' ? 'New place' : `${readOnly ? 'View' : 'Edit'} ${place?.name ?? ''}`}
      extra={
        <Space>
          {/* ⚠️ "Done", not "Close" — the Drawer's own × is already named "Close". */}
          <Button onClick={onClose}>{readOnly ? 'Done' : 'Cancel'}</Button>
          {/* Absent rather than disabled: nothing here can be saved, and a greyed Save
              under a title that says View is telling the reader twice. */}
          {!readOnly && (
            <Button type="primary" loading={save.isPending} onClick={() => form.submit()} data-testid="place-save">
              {confirmed && candidates.length > 0 ? 'Create anyway' : 'Save'}
            </Button>
          )}
        </Space>
      }
    >
      {formErrors.length > 0 && <Alert type="error" showIcon className="mb-4" message={formErrors.join(' ')} />}

      {candidates.length > 0 && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          data-testid="place-duplicate-prompt"
          message="Did you mean one of these?"
          description={
            <div className="flex flex-col gap-2">
              <ul className="m-0 list-none space-y-1 p-0">
                {candidates.map((candidate) => (
                  <li key={candidate.place.id} className="text-sm">
                    <span className="font-medium">{candidate.place.name}</span>
                    {candidate.place.formattedAddress && (
                      <span className="text-muted-foreground"> — {candidate.place.formattedAddress}</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="m-0 text-xs text-muted-foreground">
                Use an existing place where you can. Creating this one anyway is still allowed.
              </p>
            </div>
          }
        />
      )}

      <Form
        form={form}
        layout="vertical"
        disabled={readOnly}
        initialValues={{
          name: place?.name ?? '',
          aliases: place?.aliases ?? [],
          formattedAddress: place?.formattedAddress,
        }}
        onFinish={(values) => {
          // Re-parsed through the schema, so what reaches the API is the ZOD-PARSED
          // output — trimmed, defaulted, unknown keys stripped — rather than the form's
          // own view of its state.
          const parsed = submit(values);
          if (parsed.ok) save.mutate(parsed.data);
        }}
      >
        <Form.Item label="Name" {...field('name')}>
          <Input placeholder="Esplanade" data-testid="place-name" onChange={(event) => setName(event.target.value)} />
        </Form.Item>

        <Form.Item
          label="Also known as"
          extra="Other names this place answers to. Each one is searched exactly as the name is."
          {...field('aliases')}
        >
          <Input.TextArea
            rows={2}
            placeholder="Chowringhee"
            data-testid="place-aliases"
            // AntD hands a Form.Item its value directly, so the string↔array conversion
            // has to live here. `getValueFromEvent`/`getValueProps` would be the tidier
            // route, but `field()` owns the item's props and must not be spread over.
            onChange={(event) => {
              const aliases = event.target.value
                .split('\n')
                .map((alias) => alias.trim())
                .filter((alias) => alias.length > 0);
              form.setFieldValue('aliases', aliases);
            }}
          />
        </Form.Item>

        <Form.Item label="Address" {...field('formattedAddress')}>
          <Input placeholder="Optional" data-testid="place-address" />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
